import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { financialAPI, unitAPI, noticeAPI, advancedAmountAPI, corpusFundAPI } from '../../services/api';
import api from '../../services/api';
import axios from 'axios';
import '../../styles/Dashboard.css';
import '../../styles/ThemeOverrides.css';
import PageTitle from '../shared/PageTitle';
import { visitorAPI } from '../../services/api';
import { getCloseIconUrl } from '../../utils/closeIconUrl';
import { scrollToSectionTop } from '../../utils/scrollUtils';

const Dashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const { formatCurrency, settings } = useSettings();
  const closeIconColor = settings.block_title_color || '#8B4513';
  const closeIconUrl = getCloseIconUrl(closeIconColor);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});
  const themeCssRef = useRef(null); // Ref for the theme CSS element
  const [ownerData, setOwnerData] = useState({
    villa: null,
    payments: []
  });
  const [ownerAllPayments, setOwnerAllPayments] = useState([]);
  // State for owner's advance amounts
  const [ownerAdvanceAmounts, setOwnerAdvanceAmounts] = useState({
    isLoading: true,
    error: null,
    total: 0,
    data: []
  });
  // State for society financial transparency
  const [societyFinances, setSocietyFinances] = useState({
    isLoading: true,
    error: null,
    data: null,
    isMockData: true  // Set to true by default since we'll be using synthetic data
  });
  // State for corpus fund data
  const [corpusFundData, setCorpusFundData] = useState({
    isLoading: true,
    error: null,
    currentBalance: 0
  });

  const defaultData = useMemo(() => ({
    summary: {
      total_income: 0,
      total_expenses: 0,
      balance: 0,
      pendingPayments: 0
    },
    income_categories: [],
    expense_categories: [],
    recent_transactions: []
  }), []);

  // Function to fetch owner's advance amounts using the SAME method as admin financials
  const fetchOwnerAdvanceAmounts = useCallback(async () => {
    if (!ownerData.villa?.id) {
      console.log('No unit ID available for fetching advance amounts');
      setOwnerAdvanceAmounts(prev => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      setOwnerAdvanceAmounts(prev => ({ ...prev, isLoading: true, error: null }));
      console.log('🔄 Fetching advance amounts using SAME method as admin for unit:', ownerData.villa.id);

      // Use the EXACT same API call as admin financials
      const response = await financialAPI.getCompleteSummary();
      console.log('✅ Complete summary response (same as admin):', response);

      if (response.data && response.data.success) {
        const summary = response.data.data;
        console.log('📊 Complete summary data:', summary);

        // TEMPORARY: Use the organization-wide advance total (same as admin) to match the requirement
        // Later we can decide if owner should see organization-wide or unit-specific

        const organizationAdvanceTotal = summary.advancedPaymentsTotal || 0;
        console.log('💰 Using organization-wide advance total (same as admin):', organizationAdvanceTotal);

        // Also get unit-specific data for comparison/debugging
        let unitAdvanceTotal = 0;
        try {
          const allAdvanceResponse = await advancedAmountAPI.getAll();
          const allAdvanceData = allAdvanceResponse.data?.data || allAdvanceResponse.data || [];

          if (Array.isArray(allAdvanceData)) {
            // Filter advance amounts for this specific unit
            const unitAdvanceAmounts = allAdvanceData.filter(record => record.unit_id == ownerData.villa.id);
            console.log('🔍 Unit-specific advance amounts for comparison:', unitAdvanceAmounts);

            if (unitAdvanceAmounts.length > 0) {
              // Use the same calculation logic as the server (latest balance only)
              const sortedAmounts = unitAdvanceAmounts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
              const latestBalance = parseFloat(sortedAmounts[0]?.balance || 0);
              unitAdvanceTotal = latestBalance > 0 ? latestBalance : 0;

              console.log('💰 Unit-specific calculation (for comparison):', {
                unitId: ownerData.villa.id,
                totalRecords: unitAdvanceAmounts.length,
                latestRecord: sortedAmounts[0],
                latestBalance: latestBalance,
                unitTotal: unitAdvanceTotal
              });
            }
          }
        } catch (advanceError) {
          console.error('❌ Error fetching advance amounts for comparison:', advanceError);
        }

        setOwnerAdvanceAmounts({
          isLoading: false,
          error: null,
          total: organizationAdvanceTotal, // Use organization-wide total like admin
          data: []
        });

        console.log('✅ Owner advance amounts loaded (using admin method):', {
          displayingTotal: organizationAdvanceTotal,
          unitSpecificTotal: unitAdvanceTotal,
          organizationAPITotal: summary.advancedPaymentsTotal,
          unitId: ownerData.villa.id,
          note: `Now using organization total (${organizationAdvanceTotal}) instead of unit total (${unitAdvanceTotal})`
        });
      } else {
        throw new Error('Invalid API response structure');
      }
    } catch (error) {
      console.error('❌ Error fetching owner advance amounts:', error);
      setOwnerAdvanceAmounts({
        isLoading: false,
        error: error.message || 'Failed to load advance amounts',
        total: 0,
        data: []
      });
    }
  }, [ownerData.villa?.id]);

  // Function to fetch corpus fund data
  const fetchCorpusFundData = useCallback(async () => {
    try {
      setCorpusFundData(prev => ({ ...prev, isLoading: true, error: null }));
      console.log('🔄 Fetching corpus fund data from /api/corpus-fund/summary...');

      // Ensure we're calling the correct API endpoint
      const response = await corpusFundAPI.getSummary();
      console.log('✅ Corpus fund summary response:', response);
      console.log('🔍 Response URL:', response.config?.url);
      console.log('🔍 Response status:', response.status);

      // Verify this is actually the corpus fund API response
      if (response.config?.url && !response.config.url.includes('/corpus-fund')) {
        console.warn('⚠️ Warning: Response URL does not contain /corpus-fund, might be wrong API');
      }

      if (response.data && response.data.success) {
        const currentBalance = response.data.data?.balance || 0;
        console.log('💰 Corpus fund data structure:', response.data.data);
        console.log('💰 Extracted balance:', currentBalance);
        
        setCorpusFundData({
          isLoading: false,
          error: null,
          currentBalance: currentBalance
        });
        console.log('✅ Corpus fund current balance loaded:', currentBalance);
      } else {
        console.log('❌ Invalid response structure:', response.data);
        throw new Error('Invalid corpus fund API response structure');
      }
    } catch (error) {
      console.error('❌ Error fetching corpus fund data:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      
      // Check if it's a subscription/plan issue
      if (error.response?.status === 403) {
        console.log('🔒 Access denied - might be subscription plan restriction');
      }
      
      setCorpusFundData({
        isLoading: false,
        error: error.message || 'Failed to load corpus fund data',
        currentBalance: 0
      });
    }
  }, []);

  const fetchSocietyFinances = useCallback(async () => {
    setSocietyFinances(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Get the user's organization ID
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      const orgId = user.organization_id || 5; // Default to org ID 5 if none available

      // Add cache busting
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);

      const summaryResponse = await api.get('/api/financials/stats/summary', {
        params: {
          nocache: timestamp,
          organization_id: orgId
        }
      });

      if (summaryResponse && summaryResponse.data) {
        // Create a basic data structure from summary data (without synthetic transactions)
        const basicData = {
          summary: {
            total_income: summaryResponse.data.totalIncome || 0,
            total_expenses: summaryResponse.data.totalExpenses || 0,
            balance: summaryResponse.data.balance || 0,
            pendingPayments: summaryResponse.data.pendingPayments || 0,
          },
          income_categories: [],
          expense_categories: [],
          recent_transactions: []
        };

        // Update state with basic summary data but no mock data
        setSocietyFinances({
          isLoading: false,
          error: null,
          data: basicData,
          isMockData: false
        });

        // Try to get the actual transaction data
        try {
          // Try the main financial transactions endpoint first (we've given owners access to this)
          const transactionResponse = await api.get('/api/financials/stats/summary', {
            params: {
              organization_id: orgId,
              nocache: timestamp,
              t: randomStr,
              limit: 200
            },
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });

          if (transactionResponse.data && Array.isArray(transactionResponse.data) && transactionResponse.data.length > 0) {
            // Process the transaction data
            const processedData = processFinancialData(transactionResponse.data);

            // Ensure all required arrays exist
            const safeProcessedData = {
              ...defaultData,
              ...processedData,
              summary: processedData.summary || defaultData.summary,
              income_categories: Array.isArray(processedData.income_categories) ? processedData.income_categories : [],
              expense_categories: Array.isArray(processedData.expense_categories) ? processedData.expense_categories : [],
              recent_transactions: Array.isArray(processedData.recent_transactions) ? processedData.recent_transactions : []
            };

            // Use the actual data
            setSocietyFinances({
              isLoading: false,
              error: null,
              data: safeProcessedData,
              isMockData: false
            });

            return;
          }

          // If that fails, try the transparency endpoint which should work for owners
          const transparencyResponse = await api.get('/api/financials/transparency', {
            params: {
              organization_id: orgId,
              nocache: timestamp,
              t: randomStr + "_trans"
            },
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });

          if (transparencyResponse.data && transparencyResponse.data.success && transparencyResponse.data.data) {
            // Ensure all required arrays exist in transparency data
            const transparencyData = transparencyResponse.data.data;
            const safeTransparencyData = {
              ...defaultData,
              ...transparencyData,
              summary: transparencyData.summary || defaultData.summary,
              income_categories: Array.isArray(transparencyData.income_categories) ? transparencyData.income_categories : [],
              expense_categories: Array.isArray(transparencyData.expense_categories) ? transparencyData.expense_categories : [],
              recent_transactions: Array.isArray(transparencyData.recent_transactions) ? transparencyData.recent_transactions : []
            };

            // Use the processed data from the transparency endpoint
            setSocietyFinances({
              isLoading: false,
              error: null,
              data: safeTransparencyData,
              isMockData: false
            });

            return;
          }

          // Last try - get expense data specifically
          const expensesResponse = await api.get('/api/financials/expenses', {
            params: {
              organization_id: orgId,
              nocache: timestamp,
              t: randomStr + "_exp"
            },
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache',
              'Expires': '0'
            }
          });

          if (expensesResponse.data && Array.isArray(expensesResponse.data) && expensesResponse.data.length > 0) {
            // Process just the expense data
            const expenseData = {
              ...basicData,
              expense_categories: processFinancialData(expensesResponse.data).expense_categories || [],
              recent_transactions: expensesResponse.data.map(t => ({
                id: t.id,
                type: t.type,
                amount: parseFloat(t.amount) || 0,
                category: t.category || 'uncategorized',
                date: t.date,
                unit_number: t.villa_number || null,
                description: t.remarks || null,
                created_by: t.created_by_name || null
              })) || []
            };

            // Update with expense data
            setSocietyFinances({
              isLoading: false,
              error: null,
              data: expenseData,
              isMockData: false
            });

            return;
          }

          // Keep using the basic summary data, no synthetic generation
        } catch (transactionError) {
          // Set an error message that the user can understand but keep the basic data structure
          setSocietyFinances(prev => ({
            ...prev,
            isLoading: false,
            error: "Could not access financial data. The system may not have any financial records yet or you may not have permission to view them.",
            data: defaultData
          }));
        }
      }
    } catch (error) {
      setSocietyFinances({
        isLoading: false,
        error: "Could not access financial data. The system may not have any financial records yet or you may not have permission to view them.",
        data: defaultData
      });
    }
  }, [defaultData]);

  const [showModal, setShowModal] = useState(false);
  const [rawPayments, setRawPayments] = useState([]);

  // Move defaultData into useMemo to prevent recreation on every render

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const [visitors, setVisitors] = useState([]);
  const [visitorStats, setVisitorStats] = useState({
    todayCount: 0,
    activeCount: 0,
    pendingCount: 0,
    upcomingCount: 0,
    lastWeekCount: 0,
    lastMonthCount: 0,
    recentVisitorsCount: 0
  });
  // eslint-disable-next-line no-unused-vars
  const [villas, setVillas] = useState([]); // Used in visitor and complaint handling
  // eslint-disable-next-line no-unused-vars
  const [formData, setFormData] = useState({ // Used in visitor and complaint handling
    visitor_name: '',
    villa_id: '',
    purpose: '',
    phone: '',
    expected_date: formatDate(new Date(Date.now() + 24 * 60 * 60 * 1000)),
    expected_time: '14:00',
    expected_duration: '1',
    notes: ''
  });
  const [complaints, setComplaints] = useState([]);

  // Add a derived state to more reliably detect if we have units
  const [hasUnits, setHasUnits] = useState(false);



  // Track the previous villa data
  const previousVillaRef = useRef(null);

  // Add state for notices (rules and announcements)
  const [rules, setRules] = useState([]);
  const [announcements, setAnnouncements] = useState([]);

  const [setVilla] = useState(null);
  const [setSocietyInfo] = useState(null);

  // Define loadVisitors function
  const loadVisitors = async () => {
    try {
      setLoading(true);

      console.log('=== Owner Dashboard Loading Visitors ===');
      console.log('Current user:', currentUser);

      // Use the new filtered search endpoint that automatically filters by owner's villas
      const [visitorsData, statsData] = await Promise.all([
        visitorAPI.searchVisitors({
          limit: 100,
          status: 'all'
        }),
        visitorAPI.getComprehensiveStats()
      ]);

      console.log('Visitors data response:', visitorsData);
      console.log('Stats data response:', statsData);

      // Process the visitors data
      if (visitorsData.data && Array.isArray(visitorsData.data.visitors)) {
        // Ensure each visitor has proper status set
        const processedVisitors = visitorsData.data.visitors.map(visitor => ({
          ...visitor,
          status: visitor.time_out ? 'completed' :
              visitor.time_in && !visitor.approval_status ? 'pending_approval' :
                visitor.time_in && visitor.approval_status === 'approved' ? 'active' :
                  visitor.status || 'pending_approval',
          // Handle both villa_id and unit_id
          villa_id: visitor.villa_id || visitor.unit_id,
          unit_id: visitor.unit_id || visitor.villa_id,
          // Make sure IDs are integers
          id: typeof visitor.id === 'string' ? parseInt(visitor.id) : visitor.id
        }));

        setVisitors(processedVisitors);
      } else {
        setVisitors([]);
      }

      // Process the statistics data
      if (statsData.data) {
        setVisitorStats(statsData.data);
      }
    } catch (error) {
      console.error('Error fetching visitors:', error);
      setError('Failed to load visitors');
    } finally {
      setLoading(false);
    }
  };

  // Generate synthetic data from summary data - MOVE THIS UP BEFORE fetchSocietyFinances
  const generateSyntheticData = (summaryData) => {
    // Extract summary values
    const totalIncome = summaryData?.totalIncome || 0;
    const totalExpenses = summaryData?.totalExpenses || 0;
    const balance = summaryData?.balance || 0;
    const pendingPayments = summaryData?.pendingPayments || 0;

    // Always create at least these income categories
    const incomeCategories = [
      {
        category: 'maintenance',
        total_amount: totalIncome * 0.3,
        transaction_count: 4
      },
      {
        category: 'penalty',
        total_amount: totalIncome * 0.3,
        transaction_count: 1
      },
      {
        category: 'other_income',
        total_amount: totalIncome * 0.3,
        transaction_count: 1
      },
      {
        category: 'booking',
        total_amount: totalIncome * 0.1,
        transaction_count: 1
      }
    ];

    // Always create at least one expense category
    const expenseCategories = [
      {
        category: 'maintenance',
        total_amount: totalExpenses,
        transaction_count: 2
      }
    ];

    // Generate synthetic transactions
    const syntheticTransactions = [];

    // Create income transactions
    incomeCategories.forEach(category => {
      const transactionCount = category.transaction_count;
      // Only create transactions if we have a positive amount
      if (category.total_amount > 0) {
        const averageAmount = category.total_amount / transactionCount;

        for (let i = 0; i < transactionCount; i++) {
          // Randomize amounts slightly for realistic variation
          const varianceFactor = 0.9 + (Math.random() * 0.2); // Between 0.9 and 1.1
          const transactionAmount = (i === 0) ?
            category.total_amount - (averageAmount * varianceFactor * (transactionCount - 1)) : // Make first transaction balance the total
            averageAmount * varianceFactor;

          // Calculate date (distributed throughout the year)
          const daysAgo = Math.floor(Math.random() * 90) + (i * 15); // Spread transactions over time
          const transactionDate = new Date();
          transactionDate.setDate(transactionDate.getDate() - daysAgo);

          // Format transaction description based on category
          let description = "";
          switch (category.category) {
            case 'maintenance':
              description = `Maintenance fee payment for ${transactionDate.toLocaleString('default', { month: 'long' })}`;
              break;
            case 'penalty':
              description = "Late payment penalty fee";
              break;
            case 'other_income':
              description = "Miscellaneous society income";
              break;
            case 'booking':
              description = "Booking fee payment";
              break;
            default:
              description = `${category.category.charAt(0).toUpperCase() + category.category.slice(1).replace('_', ' ')} payment`;
          }

          syntheticTransactions.push({
            id: `income-${category.category}-${i}`,
            type: 'payment',
            amount: transactionAmount,
            category: category.category,
            date: transactionDate.toISOString().split('T')[0],
            unit_number: `T-${100 + Math.floor(Math.random() * 20)}`,
            description: description
          });
        }
      }
    });

    // Create expense transactions
    expenseCategories.forEach(category => {
      const transactionCount = category.transaction_count;
      // Only create transactions if we have a positive amount
      if (category.total_amount > 0) {
        const averageAmount = category.total_amount / transactionCount;

        for (let i = 0; i < transactionCount; i++) {
          // Randomize amounts slightly
          const varianceFactor = 0.9 + (Math.random() * 0.2); // Between 0.9 and 1.1 
          const transactionAmount = (i === 0) ?
            category.total_amount - (averageAmount * varianceFactor * (transactionCount - 1)) : // Make first transaction balance the total
            averageAmount * varianceFactor;

          // Calculate date (distributed throughout the year)
          const daysAgo = Math.floor(Math.random() * 90) + (i * 15); // Spread transactions over time
          const transactionDate = new Date();
          transactionDate.setDate(transactionDate.getDate() - daysAgo);

          // Specific maintenance expenses types we might see
          const maintenanceTypes = [
            "Building repairs and maintenance",
            "Common area maintenance work",
            "Society infrastructure maintenance"
          ];

          syntheticTransactions.push({
            id: `expense-${category.category}-${i}`,
            type: 'expense',
            amount: transactionAmount,
            category: category.category,
            date: transactionDate.toISOString().split('T')[0],
            description: maintenanceTypes[i % maintenanceTypes.length]
          });
        }
      }
    });

    // If we have no transactions (zero amounts), create sample ones
    if (syntheticTransactions.length === 0) {
      // Sample income
      syntheticTransactions.push({
        id: 'income-sample-1',
        type: 'payment',
        amount: 5000,
        category: 'maintenance',
        date: new Date().toISOString().split('T')[0],
        unit_number: 'T-101',
        description: 'Sample maintenance fee payment'
      });

      // Sample expense
      syntheticTransactions.push({
        id: 'expense-sample-1',
        type: 'expense',
        amount: 2000,
        category: 'maintenance',
        date: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        description: 'Sample maintenance expense'
      });
    }

    // Sort transactions by date (newest first)
    syntheticTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
      summary: {
        total_income: totalIncome,
        total_expenses: totalExpenses,
        balance: balance,
        pendingPayments: pendingPayments
      },
      income_categories: incomeCategories,
      expense_categories: expenseCategories,
      recent_transactions: syntheticTransactions
    };
  };

  // Function to process financial data
  const processFinancialData = (transactions) => {
    if (!Array.isArray(transactions) || transactions.length === 0) {
      return {
        summary: {
          total_income: 0,
          total_expenses: 0,
          balance: 0,
          pendingPayments: 0
        },
        income_categories: [],
        expense_categories: [],
        recent_transactions: []
      };
    }

    // Calculate totals
    let totalIncome = 0;
    let totalExpenses = 0;
    let pendingPayments = 0;

    // Category summaries
    const incomeCategories = {};
    const expenseCategories = {};

    // Process each transaction
    transactions.forEach(transaction => {
      // Convert amount to number safely
      const amount = parseFloat(transaction.amount) || 0;

      if (transaction.type === 'payment' || transaction.type === 'income') {
        // Income transaction
        totalIncome += amount;

        // Add to category
        const category = transaction.category || 'uncategorized';
        if (!incomeCategories[category]) {
          incomeCategories[category] = {
            category,
            total_amount: 0,
            transaction_count: 0
          };
        }

        incomeCategories[category].total_amount += amount;
        incomeCategories[category].transaction_count += 1;
      } else if (transaction.type === 'expense') {
        // Expense transaction
        totalExpenses += amount;

        // Add to category
        const category = transaction.category || 'uncategorized';
        if (!expenseCategories[category]) {
          expenseCategories[category] = {
            category,
            total_amount: 0,
            transaction_count: 0
          };
        }

        expenseCategories[category].total_amount += amount;
        expenseCategories[category].transaction_count += 1;
      }
    });

    // Sort categories by amount (descending)
    const sortedIncomeCategories = Object.values(incomeCategories)
      .sort((a, b) => b.total_amount - a.total_amount);

    const sortedExpenseCategories = Object.values(expenseCategories)
      .sort((a, b) => b.total_amount - a.total_amount);

    // Create the response structure
    return {
      summary: {
        total_income: totalIncome,
        total_expenses: totalExpenses,
        balance: totalIncome - totalExpenses,
        pendingPayments: pendingPayments
      },
      income_categories: sortedIncomeCategories,
      expense_categories: sortedExpenseCategories,
      recent_transactions: transactions.map(t => ({
        id: t.id,
        type: t.type,
        amount: parseFloat(t.amount) || 0,
        category: t.category || 'uncategorized',
        date: t.date,
        unit_number: t.villa_number || null,
        description: t.remarks || t.notes || null,
        created_by: t.created_by_name || null
      }))
    };
  };

  // Function to fetch society financial data - define BEFORE useEffect
  // Added defaultData dependency since it's used in the function

  // Force reload of data function - define with useCallback before the useEffect that uses it
  // const forceReloadData = useCallback(() => {
  //   console.log("Selective cache clearing activated");

  //   // IMPORTANT: Save the auth token before clearing cache
  //   const authToken = localStorage.getItem('token');
  //   const userData = localStorage.getItem('user');

  //   console.log("Preserving authentication data before cache clear");

  //   // Clear targeted keys only (not all localStorage)
  //   const keysToRemove = Object.keys(localStorage).filter(key => 
  //     (key.includes('villa') || 
  //     key.includes('Villa') ||
  //     key.includes('unit') ||
  //     key.includes('Unit') ||
  //     key.includes('payment') || 
  //     key.includes('owner') ||
  //     key.includes('dashboard') ||
  //     key.includes('cached') ||
  //     key.includes('data') ||
  //     key.includes('api')) && 
  //     // Don't remove authentication keys
  //     key !== 'token' && 
  //     key !== 'user' && 
  //     !key.includes('auth')
  //   );

  //   console.log("Selectively clearing cache keys:", keysToRemove);
  //   keysToRemove.forEach(key => localStorage.removeItem(key));

  //   // DON'T clear ALL localStorage - that removes authentication
  //   // Instead, clear only session storage for temporary data
  //   console.log("Clearing sessionStorage only (preserving auth in localStorage)");
  //   sessionStorage.clear();

  //   // Restore auth token if it was cleared accidentally
  //   if (authToken && !localStorage.getItem('token')) {
  //     console.log("Restoring authentication token");
  //     localStorage.setItem('token', authToken);
  //   }

  //   if (userData && !localStorage.getItem('user')) {
  //     console.log("Restoring user data");
  //     localStorage.setItem('user', userData);
  //   }

  //   // Set our specific flag to force refresh
  //   sessionStorage.setItem('dashboard_needs_refresh', 'true');
  //   sessionStorage.setItem('force_api_refresh', 'true');

  //   // Add a fresh timestamp to the URL
  //   const timestamp = Date.now();
  //   const url = new URL(window.location.href);
  //   url.searchParams.set('timestamp', timestamp);
  //   url.searchParams.set('nocache', timestamp);
  //   window.history.replaceState({}, '', url);

  //   // Show loading indicator
  //   setLoading(true);

  //   // Use a softer refresh approach that preserves auth
  //   setTimeout(() => {
  //     console.log("Executing soft reload to preserve authentication");

  //     // Refresh the data without a full page reload
  //     window.location.reload();

  //     // Only force reload if specifically requested by user pressing button
  //     // and we should alert them about potential login issues
  //     if (window.confirm("A full page reload may require you to login again. Continue with hard refresh?")) {
  //       // Navigate to the URL with cache-busting parameters
  //       window.location.href = url.href;
  //     }
  //   }, 100);
  // }, []); // Removed defaultData dependency since it's not used in the function

  // Custom styles based on theme settings
  const blockTitleStyle = {
    backgroundColor: settings.block_title_bg_color || '#f8f9fa',
    color: settings.block_title_color || '#8B4513'
  };

  // Apply theme settings globally
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await financialAPI.getOwnerPayments();
        setOwnerAllPayments(response.data);
      } catch (err) {
        setError(err);
        console.error('Error fetching payments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  useEffect(() => {

    // Create or update the CSS element
    if (!themeCssRef.current) {
      themeCssRef.current = document.createElement('style');
      themeCssRef.current.id = 'owner-dashboard-theme-styles';
      document.head.appendChild(themeCssRef.current);
    }

    // Apply CSS variables for theme settings
    themeCssRef.current.innerHTML = `
      :root {
        --sidebar-active-bg-color: ${settings.sidebar_active_bg_color || '#0d6efd'};
        --sidebar-active-text-color: ${settings.sidebar_active_text_color || '#ffffff'};
        --sidebar-bg-color: ${settings.sidebar_bg_color || '#ffffff'};
        --sidebar-text-color: ${settings.sidebar_text_color || '#212529'};
        --sidebar-hover-bg-color: ${settings.sidebar_hover_bg_color || '#f8f9fa'};
        --sidebar-hover-text-color: ${settings.sidebar_hover_text_color || '#0d6efd'};
        --block-title-bg-color: ${settings.block_title_bg_color || '#f8f9fa'};
        --block-title-color: ${settings.block_title_color || '#8B4513'};
        --primary-color: ${settings.theme_primary_color || '#0d6efd'};
      }
      
      /* Ensure the logo is visible */
      .sidebar-navigation .logo-image {
        display: block !important;
        object-fit: contain !important;
      }
      
      /* Force active menu styling */
      .sidebar-navigation .menu-item.active,
      .sidebar-navigation li.active {
        background-color: var(--sidebar-active-bg-color) !important;
      }
      
      .sidebar-navigation .menu-item.active .menu-link,
      .sidebar-navigation li.active .menu-link,
      .sidebar-navigation .menu-item.active .menu-text,
      .sidebar-navigation li.active .menu-text,
      .sidebar-navigation .menu-item.active .menu-icon,
      .sidebar-navigation li.active .menu-icon,
      .sidebar-navigation .menu-item.active i,
      .sidebar-navigation li.active i,
      .sidebar-navigation .menu-item.active span,
      .sidebar-navigation li.active span {
        color: var(--sidebar-active-text-color) !important;
      }
      
      /* Fix for the dashboard display issues with unit assignments */
      .dashboard-stats-container .stat-description {
        position: relative;
      }
      
      /* Dashboard stat value styling */
      .dashboard-stat-value {
        color: var(--primary-color) !important;
        font-weight: bold;
      }
    `;

    // Cleanup
    return () => {
      if (themeCssRef.current) {
        themeCssRef.current.remove();
        themeCssRef.current = null;
      }
    };
  }, [settings, ownerData.payments]); // Added ownerData.payments dependency since it affects the theme

  const getComplaintsCountByStatus = (complaints, status) => {
    if (!Array.isArray(complaints)) return 0;
    return complaints.filter(c => c.status === status).length;
  };

  useEffect(() => {
    if (currentUser && currentUser.role === 'owner') {
      loadVisitors();
    } else {
      // Initialize with empty arrays if not an owner
      setVisitors([]);
      setVillas([]);
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchComplaints = async () => {
      try {
        setLoading(true);

        // Get complaints data for owner
        const [complaintsData, villasData] = await Promise.all([
          api.get('/api/complaints/owner'),
          api.get('/api/villas/owner')
        ]);

        // Process the data
        if (Array.isArray(complaintsData.data)) {
          setComplaints(complaintsData.data);
        }

        if (Array.isArray(villasData.data)) {
          setVillas(villasData.data);
        }
      } catch (error) {
        console.error('Error fetching complaints:', error);
        setError('Failed to load complaints');
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && currentUser.role === 'owner') {
      fetchComplaints();
    }
  }, [currentUser]);

  // Function to check if villa assignment has changed
  const checkVillaAssignmentChange = useCallback((newVillaData) => {
    if (!previousVillaRef.current) {
      // First load, just store the data
      previousVillaRef.current = newVillaData;
      return;
    }

    // If we previously had no villa but now we do
    if (!previousVillaRef.current && newVillaData) {
      // New villa assigned!

      // Show a visible notification briefly
      setError(
        <div className="alert alert-success">
          <i className="fas fa-check-circle me-2"></i>
          <strong>New unit assigned!</strong> You have been assigned {newVillaData.unit_type || 'Villa'} {newVillaData.villa_number}.
          <button
            className="btn btn-sm btn-outline-success ms-3"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        </div>
      );
    }

    // Update the previous villa reference
    previousVillaRef.current = newVillaData;
  }, []);

  // Function to check API server health before trying to fetch data
  const checkApiServerStatus = useCallback(async () => {
    try {
      // Create minimal axios instance for status check
      const statusChecker = axios.create({
        baseURL: api.defaults.baseURL,
        timeout: 5000 // 5 second timeout for faster response
      });

      // Try to hit any endpoint to check if server is responding
      // First try authentication endpoint which should always exist
      try {
        const authResponse = await statusChecker.get('/api/auth/check', {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        return true;
      } catch (authError) {
        // If auth endpoint fails, try a simpler check - just hit the base URL
        try {
          const baseResponse = await statusChecker.get('/', {
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          return true;
        } catch (baseError) {
          console.error("Base URL check also failed:", baseError.message);
          return false;
        }
      }

      // If we get here, the server is responding
      // return true;
    } catch (error) {
      console.error("API server status check failed:", error.message);

      if (error.response) {
        // We got some response, even with error status
        return true; // Server is at least responding
      }

      // No response - server might be down
      return false;
    }
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Define fetchOwnerData with useCallback before using it in useEffect
  const fetchOwnerData = useCallback(async () => {
    setLoading(true);
    try {
      // IMPORTANT: Check for token before making requests
      const token = localStorage.getItem('token');
      if (!token) {
        console.error("No authentication token found! Cannot fetch data.");
        setError("You are not logged in. Please refresh the page and log in again.");
        setLoading(false);
        return;
      }

      console.log("Using auth token:", token.substring(0, 15) + "...");

      // Super aggressive cache busting with multiple techniques
      const noCacheHeaders = {
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        'X-Requested-With': 'XMLHttpRequest',
        'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT', // Force revalidation with ancient date
        'If-None-Match': Math.random().toString() // Ensure ETag doesn't match
      };

      // Add multiple timestamp parameters to ensure fresh data
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(7);

      // Create fresh axios instance to avoid any instance-level caching
      const freshApi = axios.create({
        baseURL: api.defaults.baseURL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`, // IMPORTANT: Explicitly set token
          ...noCacheHeaders
        }
      });

      // Setup our API responses with proper initialization
      let villaResponse = { data: [] };
      let userOrgResponse = { data: null };

      // Get the current auth token
      const authToken = localStorage.getItem('token');

      // Log what token the regular API uses
      const apiHeaders = {};
      if (api.defaults.headers) {
        Object.assign(apiHeaders, api.defaults.headers);
      }

      try {
        // First try to get organization details if we have an organization ID
        if (currentUser?.organization_id) {
          try {
            userOrgResponse = await freshApi.get(`/api/organizations/${currentUser.organization_id}`);
          } catch (orgError) {
            console.error("Error fetching organization:", orgError);
          }
        }

        // Use our consistent API function from services/api.js
        console.log("==== DASHBOARD: Using consistent unitAPI.getOwnerUnits() function ====");

        try {
          // This uses the exact same approach as the MyVillas component
          const ownerUnitsResponse = await unitAPI.getOwnerUnits();
          villaResponse = ownerUnitsResponse;
        } catch (err) {
          console.error("Error fetching owner units:", err);
          // Set an empty response as fallback
          villaResponse = { data: [] };
        }

        console.log("Final units response:", villaResponse.data);

        // Enhance each unit with organization name if available
        if (Array.isArray(villaResponse.data) && villaResponse.data.length > 0) {
          villaResponse.data = villaResponse.data.map(unit => ({
            ...unit,
            organization_name: currentUser.organization_name || 'My Organization'
          }));
        }
      } catch (error) {
        console.error("Error fetching units:", error);

        // Log more detailed error information
        if (error.response) {
          // The server responded with an error status
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
          console.error("Response headers:", error.response.headers);
        } else if (error.request) {
          // The request was made but no response was received
          console.error("No response received. Request:", error.request);
        } else {
          // Something else went wrong
          console.error("Error message:", error.message);
        }
        console.error("Error config:", error.config);

        // Set an empty response so the rest of the code can continue
        villaResponse = { data: [] };
      }

      // Make the rest of the API calls with error handling
      let paymentsResponse = { data: [] };
      // let complaintsResponse = { data: [] };
      // let visitorsResponse = { data: [] };

      try {
        // Try to get payments data using our consistent API function
        try {
          paymentsResponse = await financialAPI.getOwnerPayments();
          // Set debug info source
          setDebugInfo(prev => ({ ...prev, paymentSource: 'financialAPI.getOwnerPayments()' }));
        } catch (err) {
          console.log("Consistent API function failed, falling back:", err.message);
          // Try the payments endpoint directly
          try {
            paymentsResponse = await freshApi.get(`/api/payments/owner?nocache=${timestamp}&fresh=${randomStr}&t=${Date.now()}`);
            // Set debug info source
            setDebugInfo(prev => ({ ...prev, paymentSource: '/api/payments/owner endpoint' }));
          } catch (err) {
            console.log("Fallback to financials endpoint for payments");
            // Fallback to the financials endpoint which might have the data
            paymentsResponse = await freshApi.get(`/api/financials/owner?nocache=${timestamp}&fresh=${randomStr}&t=${Date.now()}`);
            // Set debug info source
            setDebugInfo(prev => ({ ...prev, paymentSource: '/api/financials/owner endpoint' }));
          }
        }

        // Ensure we have an array of payments
        if (!Array.isArray(paymentsResponse.data)) {
          paymentsResponse.data = [];
          // Update debug info
          setDebugInfo(prev => ({ ...prev, paymentDataInvalid: true }));
        }
      } catch (paymentError) {
        console.error("Error fetching payments:", paymentError);
        paymentsResponse.data = [];
        // Update debug info
        setDebugInfo(prev => ({ ...prev, paymentError: paymentError.message }));
      }

      try {
        // Try to get complaints data
        const complaintsData = await freshApi.get(`/api/complaints/owner?nocache=${timestamp}&fresh=${randomStr}&t=${Date.now()}`);
        if (Array.isArray(complaintsData.data)) {
          setComplaints(complaintsData.data);
        }
      } catch (complaintError) {
        console.error("Error fetching complaints:", complaintError);
      }

      try {
        // Try to get visitors data
        const visitorsData = await freshApi.get(`/api/visitors/owner?nocache=${timestamp}&fresh=${randomStr}&t=${Date.now()}`);
        if (Array.isArray(visitorsData.data)) {
          setVisitors(visitorsData.data);
        }
      } catch (visitorError) {
        console.error("Error fetching visitors:", visitorError);
      }

      // Check the villa response carefully
      console.log("Unit data from API:", villaResponse?.data);
      console.log("Payment data from API:", paymentsResponse.data);

      // Handle the case where villaResponse might be null/undefined
      // Check if we have valid units directly from the response
      const hasValidUnits = Array.isArray(villaResponse.data) && villaResponse.data.length > 0;

      // Set our derived state to track if user has units
      setHasUnits(hasValidUnits);
      console.log("Setting hasUnits state to:", hasValidUnits);

      // Log unit information for debugging
      if (hasValidUnits) {
        console.log("Valid units found:", villaResponse.data.length);
        villaResponse.data.forEach((unit, index) => {
          console.log(`Unit ${index + 1}:`, unit.villa_number,
            "Type:", unit.unit_type,
            "Organization:", unit.organization_name || unit.organization_id);
        });
      } else {
        console.log("No valid units found from API");
      }

      // Store debug info
      setDebugInfo({
        userId: currentUser?.id,
        userName: currentUser?.name,
        userEmail: currentUser?.email,
        villaCount: villaResponse?.data?.length || 0,
        hasValidUnits: hasValidUnits,
        villaResponseRaw: villaResponse,
        villaData: villaResponse?.data,
        timestamp: new Date().toISOString()
      });

      let villaData = null;
      console.log("DETAILED UNITS CHECK using API response data");
      console.log("Current user ID:", currentUser?.id);
      // If we have valid units in the API response, use those directly
      if (hasValidUnits) {
        // Sort units by villa number to be consistent with My Units page
        const sortedUnits = [...villaResponse.data].sort((a, b) => {
          // Extract numeric part if possible for better numeric sorting
          const numA = a.villa_number?.replace(/^\D+/g, '') || '';
          const numB = b.villa_number?.replace(/^\D+/g, '') || '';

          // If both have numeric parts, compare them numerically
          if (!isNaN(numA) && !isNaN(numB)) {
            return parseInt(numA) - parseInt(numB);
          }

          // Fall back to string comparison
          return (a.villa_number || '').localeCompare(b.villa_number || '');
        });

        // Use the first unit from the sorted results
        villaData = sortedUnits[0];
        console.log("Using first available unit:", villaData);

        // Add some default values for missing fields if needed
        villaData = {
          ...villaData,
          address: villaData.address || `${villaData.unit_type || 'Unit'} ${villaData.villa_number}`,
          size: villaData.size || 'N/A',
          bedrooms: villaData.bedrooms || '3',
          bathrooms: villaData.bathrooms || '2',
          built_year: villaData.built_year || '2020',
          maintenance_due: villaData.maintenance_fee || villaData.maintenance_due || 1000,
          organization_name: villaData.organization_name || userOrgResponse.data?.name || 'My Organization'
        };
      } else {
        console.log("No valid units to display");
        villaData = null;
      }

      console.log("Final unit data:", villaData);

      // No hardcoded fallback - only use actual data from the API
      console.log("Using actual data from API - no hardcoded fallbacks");

      // Log the final villa data used
      console.log("FINAL VILLA DATA USED:", villaData);

      // Store the full units data for debugging purposes
      sessionStorage.setItem('dashboard_units_data', JSON.stringify(villaResponse.data || []));
      console.log(`Dashboard units: ${villaResponse.data?.length || 0} - First unit: ${villaResponse.data?.[0]?.villa_number || 'none'}`);

      // Optional: Compare with data from My Units page if available
      try {
        const myUnitsData = sessionStorage.getItem('my_units_data');
        if (myUnitsData) {
          const parsedMyUnitsData = JSON.parse(myUnitsData);
          console.log(`My Units page previously had: ${parsedMyUnitsData.length} units - First unit: ${parsedMyUnitsData[0]?.villa_number || 'none'}`);

          // Check if data is different
          if (villaResponse.data?.length !== parsedMyUnitsData.length) {
            console.log("⚠️ Unit count mismatch between Dashboard and My Units");
          }
        }
      } catch (e) {
        console.error("Error comparing with My Units data:", e);
      }
      // Store raw payment data for pending calculation (same as Payments page)
      const rawPaymentData = paymentsResponse.data || [];
      setRawPayments(rawPaymentData);

      // In fetchOwnerData, after fetching payments and villa data, process payments with enrichPaymentsWithUnpaid
      let processedPayments = paymentsResponse.data || [];
      setOwnerData({
        villa: villaData,
        payments: processedPayments
      });

      // Update debug info with filtered villas
      setDebugInfo(prev => ({
        ...prev,
        validVillaCount: villaData ? 1 : 0,
        filteredVillaData: villaData
      }));

      // Check for villa assignment change
      checkVillaAssignmentChange(villaData);

      setError(null);
    } catch (err) {
      console.error('Error fetching owner data:', err);

      // Create a more user-friendly error message with debugging info
      let errorMsg = 'Failed to load data. Please try again.';
      let detailsMsg = '';

      if (err.response) {
        // We got a response from the server with an error status
        errorMsg += ` Server returned: ${err.response.status} ${err.response.statusText}`;
        detailsMsg = JSON.stringify(err.response.data || {});
      } else if (err.request) {
        // No response received - likely network or CORS issue
        errorMsg += ' Network error - no response from server.';
        detailsMsg = 'This could be a connection issue. Please check your network connection and try again.';
      } else {
        // Something else went wrong
        errorMsg += ' ' + (err.message || 'Unknown error');
      }

      // Set a more detailed error with debug info and reload button
      setError(
        <div>
          <h4>Error Loading Data</h4>
          <p>{errorMsg}</p>
          {detailsMsg && (
            <div className="small text-muted mt-2 mb-2">
              Details: {detailsMsg}
            </div>
          )}
          <button
            className="btn btn-primary mt-2"
            onClick={() => {
              // Clear the cache and reload data
              sessionStorage.clear();
              fetchOwnerData(true);
            }}
          >
            <i className="fas fa-sync me-2"></i> Reload Data
          </button>
        </div>
      );

      // Store detailed error information in debug
      setDebugInfo(prev => ({
        ...prev,
        error: err.message,
        errorStack: err.stack,
        errorResponse: err.response?.data,
        errorStatus: err.response?.status,
        errorStatusText: err.response?.statusText,
        errorConfig: err.config?.url,
        errorTime: new Date().toISOString()
      }));

      // Set empty data structure to prevent rendering errors
      setOwnerData({
        villa: null,
        payments: []
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser, checkVillaAssignmentChange]);

  // At the top of the component, after useState declarations
  useEffect(() => {
    console.log("=== DASHBOARD INIT ===");

    // Log the current user for debugging
    console.log("Current user in Dashboard:", currentUser);
    console.log("Current user ID:", currentUser?.id);

    // Check if API server is available first
    checkApiServerStatus().then(isAvailable => {
      if (isAvailable) {
        // Server is responding, proceed with data fetch
        console.log("API server is available, fetching data...");
        fetchOwnerData(true).then(() => {
          // Log payments after data fetch to verify they loaded
          console.log("Dashboard payments data loaded:", ownerData.payments);
        });
      } else {
        // Server appears to be down
        console.error("API server appears to be down");
        setError(
          <div className="alert alert-danger">
            <h4>API Server Unreachable</h4>
            <p>Cannot connect to the API server. Please check your network connection and server status.</p>
            <button
              className="btn btn-primary mt-2"
              onClick={() => {
                // Clear the cache and retry
                sessionStorage.clear();
                window.location.reload();
              }}
            >
              <i className="fas fa-sync me-2"></i> Retry Connection
            </button>
          </div>
        );
        setLoading(false);
      }
    });

    // Whenever component unmounts, clear the intervals
    return () => {
      console.log("Dashboard unmounting - setting refresh flags");
      sessionStorage.setItem('dashboard_needs_refresh', 'true');
      sessionStorage.setItem('force_api_refresh', 'true');
    };
  }, [fetchOwnerData, currentUser, checkApiServerStatus]);

  // Remove the interval checking My Units data changes
  useEffect(() => {
    // Only set up if we have owner data loaded
    if (!ownerData.villa || !currentUser?.id) {
      return;
    }

    return () => {
      // cleanup code here
    };
  }, [ownerData.villa, currentUser]);

  // Add useEffect for fetching society financial data
  useEffect(() => {
    // Initialize variable to track if component is mounted
    let isMounted = true;

    // Function to fetch data safely
    const fetchDataSafely = async () => {
      if (!isMounted) return;

      console.log("Fetching financial data in useEffect");
      // Call the fetchSocietyFinances function
      await fetchSocietyFinances();
      // Also fetch corpus fund data
      await fetchCorpusFundData();
      // Note: fetchOwnerAdvanceAmounts will be called after owner data is loaded
    };

    // Call the fetch function immediately
    fetchDataSafely();

    // Refresh financial data every 5 minutes
    const refreshInterval = setInterval(() => {
      if (isMounted) fetchDataSafely();
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  }, [fetchSocietyFinances, ownerData.payments, fetchCorpusFundData]); // Added ownerData.payments dependency

  // Separate useEffect for fetching advance amounts after owner data is loaded
  useEffect(() => {
    if (ownerData.villa?.id) {
      console.log('📋 Owner data loaded, fetching advance amounts for villa:', ownerData.villa.id);
      fetchOwnerAdvanceAmounts();
    }
  }, [ownerData.villa?.id, fetchOwnerAdvanceAmounts]);

  // Function to check data consistency
  const getNextPaymentInfo = () => {
    // Check for real villa data first
    if (ownerData.villa && ownerData.villa.maintenance_fee) {
      // Use actual maintenance fee from villa data
      const maintenanceFee = parseFloat(ownerData.villa.maintenance_fee);

      // Check if there are pending financials (expenses) from actual API data
      const pendingPayments = ownerData.payments?.filter(payment =>
        payment.type === 'expense' && new Date(payment.date) > new Date()
      ) || [];

      // Sort by date ascending (closest due date first)
      pendingPayments.sort((a, b) => new Date(a.date) - new Date(b.date));

      // Use the first pending payment if available
      if (pendingPayments.length > 0) {
        console.log('Found pending payment:', pendingPayments[0]);
        return {
          date: formatDate(pendingPayments[0].date),
          amount: pendingPayments[0].amount || 0
        };
      }
      // const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, settings?.maintenance_due_day);

      const lastPaidMaintenance = ownerData.payments
        .filter(payment => payment.status === "paid" && payment.type === "maintenance")
        .sort((a, b) => b.id - a.id)[0];
      const today = new Date();
      const currentMonth = today.getMonth() + 1; // 1-12 month number
      let nextMonth;
      if (lastPaidMaintenance && typeof lastPaidMaintenance.month_id === 'number') {
        const nextMonthNum = lastPaidMaintenance.month_id + 1;
        const year = today.getFullYear() + (nextMonthNum > 12 ? 1 : 0);
        const month = (nextMonthNum - 1) % 12;
        nextMonth = new Date(year, month, settings?.maintenance_due_day || 1);
      } else {
        nextMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          settings?.maintenance_due_day || 1
        );
      }
      // Check if next month payment is paid
      const nextMonthPaid = !!(lastPaidMaintenance && lastPaidMaintenance.month_id > currentMonth);

      return {
        date: formatDate(nextMonth),
        amount: maintenanceFee,
        nextMonthPaid
      };
    }

    // Fallback only if no villa data available
    const today = new Date();
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 6);

    // Check if we at least have payment history to infer a maintenance amount
    const lastPayment = ownerData.payments?.[0]?.amount;
    const defaultAmount = lastPayment || 0;

    return {
      date: formatDate(nextMonth),
      amount: defaultAmount
    };
  };

  const nextPayment = getNextPaymentInfo();

  useEffect(() => {
    setShowModal(ownerAllPayments?.total_pendings > 0);
  }, [ownerAllPayments?.total_pendings > 0]);

  // Removed the override code that was forcing T-104 to be displayed
  // This allows the actual unit data from the API to be shown

  // DOM manipulation disabled - displaying actual data from API
  useEffect(() => {
    console.log("DOM manipulation disabled - showing only API data");
  }, []);

  // Replace with a useEffect that logs the current villa data for debugging purposes
  useEffect(() => {
    const fetchSocietyInfo = async () => {
      if (!loading && ownerData.villa) {
        console.log("Current villa data:", ownerData.villa);
      }
      try {
        // First try to get the villa-specific maintenance fee
        const villaEndpoint = '/api/villas/owner';
        const villaResponse = await api.get(villaEndpoint);

        if (villaResponse.data && villaResponse.data.length > 0) {
          const villaData = villaResponse.data[0];
          setVilla(villaData); // Set villa data here to ensure it's available early

          // Check if villa has a maintenance_fee and it's not null or 0
          if (villaData.maintenance_fee !== undefined &&
            villaData.maintenance_fee !== null &&
            parseFloat(villaData.maintenance_fee) > 0) {
            const villaFee = parseFloat(villaData.maintenance_fee);
            if (!isNaN(villaFee)) {
              console.log('Setting maintenance fee from villa data:', villaFee);
              return; // Exit early if we found a valid maintenance fee in villa
            }
          }
        }

        // If no villa-specific fee, try society settings
        const settingsEndpoint = '/api/settings/public';
        console.log(`Calling API: ${settingsEndpoint}`);
        const response = await api.get(settingsEndpoint);
        setSocietyInfo(response.data); // Store society info for receipt
      } catch (err) {
        console.error("Error fetching maintenance fee:", err);
      }
    };

    fetchSocietyInfo();
  }, [loading, ownerData.villa]);

  // Additional check to ensure consistent empty state display
  useEffect(() => {
    // Double-check data consistency based on API results
    // Data consistency function - just logs info without manipulating DOM
    const checkDataConsistency = () => {
      try {
        // Log whether we have villa data or not
        if (!ownerData.villa || Object.keys(ownerData.villa).length === 0) {
          console.log('No villa data found from API');
        } else {
          console.log('Villa data found:', ownerData.villa.villa_number);
        }
      } catch (error) {
        console.error('Error checking data:', error);
      }
    };

    // Run once after a short delay to ensure data has loaded
    setTimeout(checkDataConsistency, 1000);
  }, [ownerData.villa]);

  // Debug functions removed - no longer needed

  // Fetch notices periodically
  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const [rulesRes, annRes] = await Promise.all([
          noticeAPI.getRules(),
          noticeAPI.getAnnouncements()
        ]);
        setRules(rulesRes.data);
        setAnnouncements(annRes.data);
      } catch (err) {
        console.error('Error fetching notices:', err);
      }
    };
    fetchNotices();
    const interval = setInterval(fetchNotices, 30000);
    return () => clearInterval(interval);
  }, []);

  const getLast30DaysVisitorsCount = (visitors) => {
    if (!Array.isArray(visitors)) return 0;

    const now = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(now.getDate() - 30);

    return visitors.filter(visitor => {
      let dateToCheck;

      if (visitor.time_in) {
        dateToCheck = new Date(visitor.time_in);
      } else if (visitor.expected_date && visitor.expected_time) {
        dateToCheck = new Date(`${visitor.expected_date}T${visitor.expected_time}`);
      } else {
        return false;
      }

      return dateToCheck >= thirtyDaysAgo && dateToCheck <= now;
    }).length;
  };

  const generateUpiLink = (upi, name) => {
    if (!upi || !name) return '#';
    return `upi://pay?pa=${upi}&pn=${encodeURIComponent(name)}&cu=INR`;
  };

  // Dedicated pagination state for transactions
  const [transactionsPage, setTransactionsPage] = useState(1);
  const transactionsPerPage = 20;
  const allTransactions = societyFinances.data?.recent_transactions || [];
  const transactionsIndexOfLastItem = transactionsPage * transactionsPerPage;
  const transactionsIndexOfFirstItem = transactionsIndexOfLastItem - transactionsPerPage;
  const currentTransactions = allTransactions.slice(transactionsIndexOfFirstItem, transactionsIndexOfLastItem);
  const totalTransactionsPages = Math.ceil(allTransactions.length / transactionsPerPage);
  const paginateTransactions = (pageNumber) => {
    setTransactionsPage(pageNumber);
    setTimeout(() => {
      scrollToSectionTop('transactions-section');
    }, 50);
  };
  const previousTransactionsPage = () => {
    if (transactionsPage > 1) {
      setTransactionsPage(transactionsPage - 1);
      setTimeout(() => {
        scrollToSectionTop('transactions-section');
      }, 50);
    }
  };
  const nextTransactionsPage = () => {
    if (transactionsPage < totalTransactionsPages) {
      setTransactionsPage(transactionsPage + 1);
      setTimeout(() => {
        scrollToSectionTop('transactions-section');
      }, 50);
    }
  };

  if (loading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center mt-5">
        <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div className="text-center">
          <h4>Loading latest unit information...</h4>
          <p className="text-muted">Refreshing data to show your most recent unit assignments.</p>
          <div className="progress mt-2" style={{ height: "10px", width: "300px" }}>
            <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: "100%" }}></div>
          </div>
        </div>
      </div>
    );
  }

  // If there's an error, show error message
  if (error && !ownerData.villa) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4>Error Loading Data</h4>
          <p>{error}</p>
          {/* <button 
            className="btn btn-primary mt-3" 
            onClick={forceReloadData}
          >
            <i className="fas fa-sync me-2"></i> Reload Data
          </button> */}
        </div>
      </div>
    );
  }
  // Add debug panel if needed - comment out in production
  const showDebugInfo = false; // Debug panel disabled
  console.log("ownerAllPayments", ownerAllPayments);

  return (
    <div className="container-fluid mt-4 activity-container">
      {/* Debug info panel */}
      {showDebugInfo && (
        <div className="alert alert-info mb-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Debug Info:</strong>
              <span className="badge bg-primary ms-2">Has Units: {hasUnits ? 'YES' : 'NO'}</span>
              <span className="badge bg-secondary ms-2">Villa Count: {debugInfo.villaCount || 0}</span>
              <span className="badge bg-info ms-2">Last Updated: {new Date().toLocaleTimeString()}</span>
            </div>
            {/* <div>
              <button 
                className="btn btn-sm btn-primary" 
                onClick={forceReloadData}
              >
                Force Hard Refresh
              </button>
            </div> */}
          </div>
          {/* Display full settings object for debugging */}
          <pre style={{ maxHeight: '200px', overflow: 'auto', background: '#ffffff', color: '#000', padding: '10px', marginTop: '10px' }}>
            {JSON.stringify(settings, null, 2)}
          </pre>
        </div>
      )}

      {/* Owner Dashboard Header */}
      <PageTitle
        icon="fa-home"
        title={`Welcome, ${currentUser ? currentUser.name : 'Property Owner'}`}
        action={
          <div className="d-flex align-items-center">
            {/* <button 
              className="btn btn-outline-primary me-3" 
              onClick={forceReloadData}
              disabled={loading}
            >
              {loading ? (
                <><span className="spinner-border spinner-border-sm me-1"></span> Refreshing...</>
              ) : (
                <><i className="fas fa-sync-alt me-1"></i> Refresh Data</>
              )}
            </button> */}
            <span className="badge bg-success">Resident</span>
          </div>
        }
      />

      {/* Welcome message */}
      <div className="alert alert-info mb-4">
        <div className="d-flex align-items-center">
          <i className="fas fa-info-circle fa-2x me-3"></i>
          <div>
            <h5 className="mb-1">Welcome to Resident Portal</h5>
            <p className="mb-0">
              Manage your property, make payments, file complaints and track visitor information all in one place.
            </p>
          </div>
        </div>
      </div>

      {/* Dashboard Stats Summary */}
      <div className="dashboard-stats-container">
        {/* My Unit card - displays first assigned unit */}
        <div className="dashboard-stat-card units">
          <div className="stat-title">MY UNIT</div>
          {ownerData.villa ? (
            <>
              <div className="stat-value" style={{ fontSize: '1.8rem' }}>
                {ownerData.villa.villa_number}
              </div>
              <div className="stat-description">
                {ownerData.villa.unit_type || 'Unit'} • {ownerData.villa.organization_name || 'My Organization'}
              </div>
            </>
          ) : (
            <>
              <div className="stat-value" style={{ fontSize: '1.5rem' }}>
                No Units Found
              </div>
              {/* <div className="stat-description">
                <button 
                  className="btn btn-sm btn-primary mt-1 w-100" 
                  onClick={forceReloadData}
                >
                  <i className="fas fa-sync-alt me-1"></i> Refresh Data
                </button>
              </div> */}
            </>
          )}
        </div>

        <div className="dashboard-stat-card complaints">
          <div className="stat-title">PENDING PAYMENT</div>
          <div className="stat-value">
            {formatCurrency(ownerAllPayments?.total_pendings)}
          </div>
          {ownerAllPayments?.payments?.length > 0 ? (
            <div className="stat-description">
              {ownerAllPayments?.total_pendings > 0 ? (
                <span className="badge bg-danger">Deposit immediately</span>
              ) : (
                <>
                  Next due on : {nextPayment.date}
                </>
              )}
            </div>
          ) : (
            <></>
          )}
        </div>

        <div className="dashboard-stat-card visitors">
          <div className="stat-title">OPEN COMPLAINTS / SUGGESTIONS</div>
          <div className="stat-value">
            {/* {ownerData.complaints?.filter(c => c.status === 'open').length || 0} */}
            {getComplaintsCountByStatus(complaints, 'open')}
          </div>
          <div className="stat-description">
            Total complaints / suggestions: {complaints?.length || 0}
          </div>
        </div>

        <div className="dashboard-stat-card users">
          <div className="stat-title">RECENT VISITORS</div>
          <div className="stat-value">
            {visitorStats.recentVisitorsCount}
          </div>
          <div className="stat-description">
            In the last 7 days
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className='card shadow mb-4 h-100'>
        <div className="row">
          <div className="col-12">
            <div className="dashboard-card">
              <div className="card-header" style={blockTitleStyle}>
                <h5 className="mb-0">Quick Actions</h5>
              </div>
              <div className="card-body pb-0">
                <div className="row">
                  <div className="col-lg-3 col-md-6 mb-2">
                    <Link to="/owner/complaints" className="btn btn-danger w-100 d-flex align-items-center justify-content-between">
                      <span>File Complaint / Suggestion</span>
                      <i className="fas fa-exclamation-circle"></i>
                    </Link>
                  </div>
                  <div className="col-lg-3 col-md-6 mb-2">
                    <Link to="/owner/payments" className="btn btn-success w-100 d-flex align-items-center justify-content-between">
                      <span>Make Payment</span>
                      <i className="fas fa-money-bill-wave"></i>
                    </Link>
                  </div>
                  <div className="col-lg-3 col-md-6 mb-2">
                    <Link to="/owner/visitors" className="btn btn-info w-100 d-flex align-items-center justify-content-between">
                      <span>Manage Visitors</span>
                      <i className="fas fa-users"></i>
                    </Link>
                  </div>
                  <div className="col-lg-3 col-md-6 mb-2">
                    <Link to="/profile" className="btn btn-secondary w-100 d-flex align-items-center justify-content-between">
                      <span>My Profile</span>
                      <i className="fas fa-user-cog"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Property Information Card */}
      <div className="row">
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="card shadow h-100">
            <div className="card-header d-flex flex-wrap" style={blockTitleStyle}>
              <h5 className="mb-0">
                <i className="fas fa-home me-2"></i>
                My Property Information
              </h5>
            </div>
            <div className="card-body">
              {ownerData.villa ? (
                <div>
                  <h4 className="mb-3">{ownerData.villa.unit_type || 'Unit'} {ownerData.villa.villa_number}</h4>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-building me-2"></i> Organization:</span>
                      <span className="fw-bold">{settings?.society_name || ownerData.villa.organization_name || currentUser?.organization_name || 'My Organization'}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-id-card me-2"></i> Unit ID:</span>
                      <span className="fw-bold">{ownerData.villa.id}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-hashtag me-2"></i> Number:</span>
                      <span className="fw-bold">{ownerData.villa.villa_number}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span><i className="fas fa-home me-2"></i> Type:</span>
                      <span className="fw-bold">{ownerData.villa.unit_type || 'Unit'}</span>
                    </div>
                    {ownerData.villa.size && (
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-expand-arrows-alt me-2"></i> Size:</span>
                        <span className="fw-bold">{ownerData.villa.size}</span>
                      </div>
                    )}
                    {ownerData.villa.status && (
                      <div className="d-flex justify-content-between mb-2">
                        <span><i className="fas fa-info-circle me-2"></i> Status:</span>
                        <span className="fw-bold">{ownerData.villa.status || 'Active'}</span>
                      </div>
                    )}
                  </div>
                  <div className="alert alert-info d-block">
                    <div className="d-flex flex-wrap justify-content-between align-items-center">
                      <div>
                        <strong>Monthly Maintenance:</strong>
                        <div className="fs-5">{formatCurrency(ownerData.villa.maintenance_fee || 0)}</div>
                      </div>
                      <Link to="/owner/payments" className="btn btn-sm btn-primary mt-2">
                        View Payments
                      </Link>
                    </div>
                    {ownerAllPayments?.last_payment_date && (
                      <div className="mt-2 small">
                        <strong>Last Payment:</strong> {ownerAllPayments?.last_payment_date}
                      </div>
                    )}
                  </div>

                  {/* <div className="d-grid mt-3">
                    <Link to="/owner/my-units" className="btn btn-outline-primary btn-block">
                      <i className="fas fa-building me-2"></i> View All My Units
                    </Link>
                  </div> */}
                </div>
              ) : (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  We couldn&apos;t find any units assigned to your account. If you believe this is an error, please try refreshing or contact support.
                </div>
              )}
            </div>
            <div className="card-footer bg-light">
              <Link to="/owner/my-units" className="btn btn-outline-primary w-100">
                Check My Units
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Visitors Card */}
        <div className="col-lg-6 col-md-12 mb-4">
          <div className="card shadow h-100">
            <div className="card-header" style={blockTitleStyle}>
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-user-friends me-2"></i>
                  Recent Visitors
                </h5>
                <Link to="/owner/visitors" className="btn btn-sm btn-outline-secondary searchFilterButton d-flex align-items-center">
                  <i className="fas fa-plus me-1"></i> Register
                </Link>
              </div>
            </div>
            <div className="card-body">
              {getLast30DaysVisitorsCount(visitors) > 0 && visitors.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Visitor</th>
                        <th>Date</th>
                        <th>Purpose</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitors
                        .filter(visitor => visitor.time_in || visitor.time_out)
                        .slice(0, 5)
                        .map(visitor => (
                          <tr key={visitor.id}>
                            <td>{visitor.name || visitor.visitor_name}</td>
                            <td>{
                              formatDate(visitor.date || visitor.time_in) !== 'N/A'
                                ? formatDate(visitor.date || visitor.time_in)
                                : formatDate(`${visitor.expected_date}T${visitor.expected_time}` || new Date().toISOString())
                            }</td>
                            <td>{visitor.purpose}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  No recent visitors found.
                </div>
              )}
            </div>
            <div className="card-footer bg-light">
              <Link to="/owner/visitors" className="btn btn-outline-info w-100">
                Manage Visitors
              </Link>
            </div>
          </div>
        </div>

        {/* Paying details Card */}
        <div className="col-lg-12 col-md-12 mb-4">
          <div className="card shadow h-100 border-0">
            <div className="card-header d-flex align-items-center" style={blockTitleStyle}>
              <i className="fas fa-qrcode me-2 fs-4"></i>
              <div>
                <h5 className="mb-0 fw-bold">Pay Maintenance</h5>
              </div>
            </div>
            {settings.scanner_qr_url ? (
              <div className="d-flex flex-wrap card-body text-center">
                <div className="responsive-width">
                  <div className='w-100'>
                    <img
                      src={settings?.scanner_qr_url}
                      alt="Scan this QR code to pay your society maintenance via UPI"
                      className="img-fluid rounded shadow-sm border"
                      style={{ maxWidth: '55%', background: '#fff', padding: '8px' }}
                    />
                  </div>
                </div>
                <div className="responsive-width">
                  {(settings?.scanner_owner_name || settings?.scanner_upi_id || settings?.scanner_upi_number) && (
                    <div className="p-3 rounded w-100" style={{ background: "#f8f9fa", display: "inline-block" }}>
                      {settings?.scanner_owner_name && (
                        <span className="fw-bold" style={{ fontSize: "1.1rem" }}>
                          {settings?.scanner_owner_name}
                          <span className="ms-1" title="Verified">
                            <i className="fas fa-badge-check text-primary" style={{ color: "#2196f3" }}></i>
                          </span>
                        </span>
                      )}
                      <div className="small text-muted mt-1">
                        {settings?.scanner_upi_id && (
                          <>
                            <span>UPI ID: <span className="text-dark">{settings?.scanner_upi_id}</span></span><br />
                          </>
                        )}
                        {settings?.scanner_upi_number && (
                          <span>Number: <span className="text-dark">{settings?.scanner_upi_number}</span></span>
                        )}
                      </div>
                    </div>
                  )}
                  {settings.scanner_qr_url && (
                    <div className="mt-3 w-100">
                      <a
                        href={settings?.scanner_qr_url}
                        download="My-Society-UPI-QR.png"
                        className="d-block btn btn-sm btn-outline-secondary py-3"
                        aria-label="Download QR code"
                      >
                        <i className="fas fa-download me-1"></i> Download QR
                      </a>
                    </div>
                  )}
                  <p className="text-muted my-3">Scan the QR code using your UPI app or tap a button below:</p>
                  <div className="d-grid gap-2">
                    {settings?.scanner_upi_id && (
                      <>
                        <a
                          href={generateUpiLink(settings?.scanner_upi_id, settings?.scanner_owner_name)}
                          className="btn btn-outline-primary btn-lg d-flex align-items-center justify-content-center"
                          aria-label="Pay with Google Pay"
                        >
                          <i className="fab fa-google-pay me-2 fs-5"></i> Google Pay
                        </a>
                        <a
                          href={generateUpiLink(settings?.scanner_upi_id, settings?.scanner_owner_name)}
                          className="btn btn-outline-dark btn-lg d-flex align-items-center justify-content-center"
                          aria-label="Pay with PhonePe"
                        >
                          <i className="fas fa-mobile-alt me-2 fs-5"></i> PhonePe
                        </a>
                        <a
                          href={generateUpiLink(settings?.scanner_upi_id, settings?.scanner_owner_name)}
                          className="btn btn-outline-success btn-lg d-flex align-items-center justify-content-center"
                          aria-label="Pay with Paytm"
                        >
                          <i className="fas fa-wallet me-2 fs-5"></i> Paytm
                        </a>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="alert alert-info mx-3">
                  <i className="fas fa-info-circle me-2"></i>
                  No data found.
                </div>
              </>
            )}
            {settings.scanner_qr_url && (
              <div className="card-footer bg-light text-center">
                <small className="text-muted">
                  UPI apps will open only on mobile. For desktop, scan the QR code.
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Society Financial Transparency Section */}

      <div className="row mt-4">
        <div className="col-12">
          <div className="card shadow" id="transactions-section">
            <div className="card-header" style={blockTitleStyle}>
              <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <h5 className="mb-0">
                  <i className="fas fa-chart-pie me-2"></i>
                  Society Financial Transparency (FY {new Date().getFullYear()})
                </h5>
                <div className="d-flex">
                  {societyFinances.error && (
                    <button
                      className="btn btn-sm btn-outline-warning me-2"
                      onClick={() => {
                        const syntheticData = generateSyntheticData({
                          totalIncome: societyFinances.data?.summary?.total_income || 60000,
                          totalExpenses: societyFinances.data?.summary?.total_expenses || 16000,
                          balance: societyFinances.data?.summary?.balance || 44000,
                          pendingPayments: societyFinances.data?.summary?.pendingPayments || 20000
                        });
                        setSocietyFinances({
                          isLoading: false,
                          error: null,
                          data: syntheticData,
                          isMockData: true
                        });
                      }}
                      title="Generate sample data for demonstration"
                    >
                      <i className="fas fa-vial me-1"></i> Use Sample Data
                    </button>
                  )}
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSocietyFinances(prev => ({ ...prev, isLoading: true }));
                      setTimeout(() => {
                        fetchSocietyFinances();
                        fetchOwnerAdvanceAmounts();
                        fetchCorpusFundData();
                      }, 100);
                    }}
                    disabled={societyFinances.isLoading}
                    title="Refresh financial data"
                  >
                    {societyFinances.isLoading ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span> Loading...</>
                    ) : (
                      <><i className="fas fa-sync-alt me-1"></i> Refresh Data</>
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body">
              {/* Removed mock data warning */}

              {/* Success message removed */}

              {societyFinances.isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading financial data...</p>

                  <button
                    className="btn btn-outline-danger btn-sm mt-2"
                    onClick={() => {
                      fetchSocietyFinances();
                      fetchOwnerAdvanceAmounts();
                      fetchCorpusFundData();
                    }}
                  >
                    <i className="fas fa-sync me-1"></i> Retry Loading Data
                  </button>
                </div>
              ) : societyFinances.error ? (
                <div className="alert alert-danger">
                  <div className="d-flex mb-2">
                    <i className="fas fa-exclamation-triangle me-2 fa-2x"></i>
                    <div>
                      <h5>Data Access Error</h5>
                      <p>{societyFinances.error}</p>
                    </div>
                  </div>

                  <div className="mt-2">
                    <p>Common causes of this issue:</p>
                    <ul>
                      <li>The database doesn&apos;t have any financial transactions for your society yet</li>
                      <li>Your user account doesn&apos;t have the right permissions</li>
                      <li>The server couldn&apos;t process the financial data</li>
                    </ul>
                  </div>

                  {/* Show more detailed error info if available */}
                  {societyFinances.errorDetails && (
                    <div className="mt-2 small text-muted">
                      <p>Technical details (for support):</p>
                      <pre className="bg-light p-2" style={{ maxHeight: '100px', overflow: 'auto' }}>
                        {JSON.stringify(societyFinances.errorDetails, null, 2)}
                      </pre>
                    </div>
                  )}

                  <div className="mt-3 d-flex gap-2">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => {
                        fetchSocietyFinances();
                        fetchOwnerAdvanceAmounts();
                        fetchCorpusFundData();
                      }}
                    >
                      <i className="fas fa-sync me-1"></i> Retry Loading Data
                    </button>

                    <button
                      className="btn btn-outline-warning btn-sm"
                      onClick={() => {
                        const syntheticData = generateSyntheticData({
                          totalIncome: societyFinances.data?.summary?.total_income || 60000,
                          totalExpenses: societyFinances.data?.summary?.total_expenses || 16000,
                          balance: societyFinances.data?.summary?.balance || 44000,
                          pendingPayments: societyFinances.data?.summary?.pendingPayments || 20000
                        });
                        setSocietyFinances({
                          isLoading: false,
                          error: null,
                          data: syntheticData,
                          isMockData: true
                        });
                      }}
                    >
                      <i className="fas fa-vial me-1"></i> Use Sample Data
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Financial Summary Cards */}
                  <div className="row mb-4">
                    <div className="col-md-3 mb-3">
                      <div className="card bg-light h-100">
                        <div className="card-body text-center">
                          <h6 className="text-muted mb-2">Total Income</h6>
                          <h3 className="text-success mb-0">{formatCurrency(societyFinances.data?.summary?.total_income || 0)}</h3>
                          <small className="text-muted">{new Date().getFullYear()}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card bg-light h-100">
                        <div className="card-body text-center">
                          <h6 className="text-muted mb-2">Total Expenses</h6>
                          <h3 className="text-danger mb-0">{formatCurrency(societyFinances.data?.summary?.total_expenses || 0)}</h3>
                          <small className="text-muted">{new Date().getFullYear()}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card bg-light h-100">
                        <div className="card-body text-center">
                          <h6 className="text-muted mb-2">Current Balance</h6>
                          <h3 className="text-primary mb-0">{formatCurrency(societyFinances.data?.summary?.balance || 0)}</h3>
                          {(ownerAdvanceAmounts.total > 0 || process.env.NODE_ENV === 'development') && (
                            <div className="mt-2">
                              <span className={`badge rounded-pill py-2 ${ownerAdvanceAmounts.total > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                + Advance {formatCurrency(ownerAdvanceAmounts.total)}
                              </span>
                            </div>
                          )}

                          {/* Corpus Fund Balance */}
                          {corpusFundData.isLoading ? (
                            <div className="mt-2">
                              <span className="badge rounded-pill py-2 bg-secondary">
                                <i className="fas fa-spinner fa-spin me-1"></i> Loading...
                              </span>
                            </div>
                          ) : corpusFundData.error ? (
                            <div className="mt-2">
                              <span className="badge rounded-pill py-2 bg-warning" title={corpusFundData.error}>
                                <i className="fas fa-exclamation-triangle me-1"></i> Error
                              </span>
                            </div>
                          ) : corpusFundData.currentBalance > 0 ? (
                            <div className="mt-2">
                              <span className="badge rounded-pill py-2 bg-success">
                                + Corpus Fund {formatCurrency(corpusFundData.currentBalance)}
                              </span>
                            </div>
                          ) : corpusFundData.error && corpusFundData.error.includes('403') ? (
                            <div className="mt-2">
                              <span className="badge rounded-pill py-2 bg-secondary" title="Corpus fund feature requires Pro plan">
                                <i className="fas fa-lock me-1"></i> Pro Plan Required
                              </span>
                            </div>
                          ) : (
                            // Show a placeholder when no corpus fund data is available
                            <div className="mt-2">
                              <span className="badge rounded-pill py-2 bg-light text-dark" title="Corpus fund data not available">
                                <i className="fas fa-info-circle me-1"></i> No Corpus Fund Data
                              </span>
                            </div>
                          )}

                          <small className="text-muted">As of {new Date().toLocaleDateString()}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3 mb-3">
                      <div className="card bg-light h-100">
                        <div className="card-body text-center">
                          <h6 className="text-muted mb-2">Total Pendings</h6>
                          <h3 className="text-warning mb-0">{formatCurrency(societyFinances.data?.summary?.pendingPayments)}</h3>
                          <small className="text-muted">As of {new Date().toLocaleDateString()}</small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* If there's a permission error, show it prominently */}
                  {societyFinances.error && (
                    <div className="alert alert-warning mb-4">
                      <div className="d-flex">
                        <i className="fas fa-exclamation-triangle me-3 fa-2x"></i>
                        <div>
                          <h5>Access Limited</h5>
                          <p>{societyFinances.error}</p>
                          <p className="mb-0">
                            <small>While you can see the summary figures, detailed breakdowns require additional permissions.</small>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Expense Breakdown */}
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <h5 className="mb-3">Expense Breakdown</h5>
                      {(societyFinances.data &&
                        societyFinances.data.expense_categories &&
                        Array.isArray(societyFinances.data.expense_categories) &&
                        societyFinances.data.expense_categories.length > 0) ? (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Category</th>
                                <th>Amount</th>
                                <th>Count</th>
                                <th>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {societyFinances.data.expense_categories.map((category, index) => (
                                <tr key={index}>
                                  <td className="text-capitalize">{category.category.replace('_', ' ')}</td>
                                  <td>{formatCurrency(category.total_amount)}</td>
                                  <td>{category.transaction_count}</td>
                                  <td>
                                    {societyFinances.data.summary.total_expenses ?
                                      Math.round((category.total_amount / societyFinances.data.summary.total_expenses) * 100) + '%'
                                      : '0%'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {societyFinances.isMockData && (
                            <div className="alert alert-info mt-2 mb-0">
                              <small><i className="fas fa-info-circle me-1"></i> This data is synthetically generated for demonstration purposes.</small>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle me-2"></i>
                          {societyFinances.error ?
                            "Detailed expense data is not available due to permission restrictions." :
                            (societyFinances.isMockData === true ?
                              "Expense details are currently generated from summary data as detailed access requires administrative privileges." :
                              "No expense data available. Financial transactions for expenses haven't been recorded yet.")}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6 mb-3">
                      <h5 className="mb-3">Income Sources</h5>
                      {(societyFinances.data &&
                        societyFinances.data.income_categories &&
                        Array.isArray(societyFinances.data.income_categories) &&
                        societyFinances.data.income_categories.length > 0) ? (
                        <div className="table-responsive">
                          <table className="table table-sm table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Category</th>
                                <th>Amount</th>
                                <th>Count</th>
                                <th>%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {societyFinances.data.income_categories.map((category, index) => (
                                <tr key={index}>
                                  <td className="text-capitalize">{category.category.replace('_', ' ')}</td>
                                  <td>{formatCurrency(category.total_amount)}</td>
                                  <td>{category.transaction_count}</td>
                                  <td>
                                    {societyFinances.data.summary.total_income ?
                                      Math.round((category.total_amount / societyFinances.data.summary.total_income) * 100) + '%'
                                      : '0%'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {societyFinances.isMockData && (
                            <div className="alert alert-info mt-2 mb-0">
                              <small><i className="fas fa-info-circle me-1"></i> This data is synthetically generated for demonstration purposes.</small>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          <i className="fas fa-info-circle me-2"></i>
                          {societyFinances.error ?
                            "Detailed income data is not available due to permission restrictions." :
                            (societyFinances.isMockData === true ?
                              "Income details are currently generated from summary data as detailed access requires administrative privileges." :
                              "No income data available. Financial transactions for income haven't been categorized yet.")}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Recent Transactions */}
                  {settings?.show_society_transactions === true && (
                    <div className="row">
                      <div className="col-12">
                        <h5 className="mb-3">Recent Society Transactions</h5>
                        {console.log('societyFinances.data.recent_transactions', societyFinances.data.recent_transactions)}
                        {/* {console.log("RENDER: recent_transactions=", societyFinances.data?.recent_transactions, "isMockData=", societyFinances.isMockData)} */}
                        {(societyFinances.data &&
                          societyFinances.data.recent_transactions &&
                          Array.isArray(societyFinances.data.recent_transactions) &&
                          societyFinances.data.recent_transactions.length > 0) ? (
                          <div className="table-responsive">
                            <table className="table table-striped table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Date</th>
                                  <th>Type</th>
                                  <th>Category</th>
                                  <th>Amount</th>
                                  <th>Unit</th>
                                  <th>Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {currentTransactions.map((transaction, index) => (
                                  <tr key={transaction.id || index}>
                                    <td>{formatDate(transaction.date)}</td>
                                    <td>
                                      {transaction.type === 'payment' || transaction.type === 'income' ?
                                        <span className="badge bg-success">Income</span> :
                                        <span className="badge bg-danger">Expense</span>}
                                    </td>
                                    <td className="text-capitalize">{transaction.category?.replace('_', ' ') || 'Uncategorized'}</td>
                                    <td>{formatCurrency(transaction.amount)}</td>
                                    <td>{transaction.unit_number || transaction.villa_number || '-'}</td>
                                    <td>{transaction.description || transaction.notes || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            {societyFinances.isMockData && (
                              <div className="alert alert-info mt-2 mb-0">
                                <small><i className="fas fa-info-circle me-1"></i> This data is synthetically generated for demonstration purposes.</small>
                              </div>
                            )}
                            {/* Pagination */}
                            {currentTransactions.length > 0 && (
                              <div className="card-footer d-flex justify-content-between align-items-center">
                                <div>
                                  Showing {allTransactions.length === 0 ? 0 : transactionsIndexOfFirstItem + 1} to {Math.min(transactionsIndexOfLastItem, allTransactions.length)} of {allTransactions.length} transactions
                                </div>

                                <nav aria-label="Transaction pagination">
                                  <ul className="pagination mb-0">
                                    <li className={`page-item ${transactionsPage === 1 ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={previousTransactionsPage} disabled={transactionsPage === 1}>
                                        <i className="fas fa-chevron-left"></i>
                                      </button>
                                    </li>

                                    {/* Simplified pagination - show only certain page numbers */}
                                    {Array.from({ length: totalTransactionsPages }).map((_, index) => {
                                      const pageNumber = index + 1;

                                      // Show first page, last page, current page, and pages around current page
                                      if (
                                        pageNumber === 1 ||
                                        pageNumber === totalTransactionsPages ||
                                        (pageNumber >= transactionsPage - 1 && pageNumber <= transactionsPage + 1)
                                      ) {
                                        return (
                                          <li key={pageNumber} className={`page-item ${transactionsPage === pageNumber ? 'active' : ''}`}>
                                            <button className="page-link" onClick={() => paginateTransactions(pageNumber)}>
                                              {pageNumber}
                                            </button>
                                          </li>
                                        );
                                      }

                                      // Show ellipsis
                                      if (
                                        pageNumber === 2 && transactionsPage > 3 ||
                                        pageNumber === totalTransactionsPages - 1 && transactionsPage < totalTransactionsPages - 2
                                      ) {
                                        return (
                                          <li key={pageNumber} className="page-item disabled">
                                            <span className="page-link">...</span>
                                          </li>
                                        );
                                      }

                                      return null;
                                    })}

                                    <li className={`page-item ${transactionsPage === totalTransactionsPages ? 'disabled' : ''}`}>
                                      <button className="page-link" onClick={nextTransactionsPage} disabled={transactionsPage === totalTransactionsPages}>
                                        <i className="fas fa-chevron-right"></i>
                                      </button>
                                    </li>
                                  </ul>
                                </nav>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="alert alert-info">
                            <i className="fas fa-info-circle me-2"></i>
                            {societyFinances.error ?
                              "Detailed transaction data is not available due to permission restrictions." :
                              (societyFinances.isMockData === true ?
                                "Transaction details are currently generated from summary data as detailed access requires administrative privileges." :
                                "No recent transactions available. Financial activity has not been recorded in the system yet.")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Notices Section */}
      <div className="row mt-4" id="notices">
        <div className="col-12">
          <h2 className="section-title mb-3"><i className="fas fa-bell me-2"></i>Notices</h2>
          <div className="row">
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header d-flex align-items-center" style={blockTitleStyle}>
                  <i className="fas fa-gavel me-2"></i>
                  <span>Rules</span>
                </div>
                <div className="card-body">
                  {rules.length === 0 ? (
                    <p>No rules available.</p>
                  ) : (
                    <ul className="list-group">
                      {rules.map(rule => (
                        <li key={rule.id} className="list-group-item border-0 px-0">
                          <h6 className="mb-1">{rule.title}</h6>
                          <small className="text-muted">{rule.description}</small>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card mb-3">
                <div className="card-header d-flex align-items-center" style={blockTitleStyle}>
                  <i className="fas fa-bullhorn me-2"></i>
                  <span>Announcements</span>
                </div>
                <div className="card-body">
                  {announcements.length === 0 ? (
                    <p>No announcements available.</p>
                  ) : (
                    <ul className="list-group">
                      {announcements.map(ann => (
                        <li key={ann.id} className="list-group-item border-0 px-0">
                          <h6 className="mb-1">{ann.title}</h6>
                          <small className="text-muted">
                            {new Date(ann.start_date).toLocaleDateString()}
                            {ann.expiry_date ? ` - ${new Date(ann.expiry_date).toLocaleDateString()}` : ''}
                          </small>
                          <p className="mt-2">{ann.description}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Only render debug panels if explicitly requested (which is now set to false by default) */}
      {false && showDebugInfo && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="card bg-light">
              <div className="card-header" style={blockTitleStyle}>
                <h5 className="mb-0">Debug Information</h5>
              </div>
              <div className="card-body">
                <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Payment Reminder Modal */}
      {showModal && (
        <div>
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: settings.block_title_bg_color || '#f8f9fa', color: settings.block_title_color || '#8B4513' }}>
                  <h5 className="modal-title">Payment Reminder</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                    style={{ backgroundImage: closeIconUrl, fontSize: '18px', opacity: '0.9' }}
                  ></button>
                </div>
                <div className="modal-body">
                  <p>
                    Dear {`${currentUser ? currentUser.name : 'Property Resident'}`},
                    <br /><br />
                    Our records show that your maintenance payment for the (previous/upcoming) month is still pending.
                    Please ensure the payment is made by the due date to avoid any late fees or service interruptions.
                    <br /><br />
                    Thank you for your prompt attention to this matter.
                    <br /><br />
                    If you have already made the payment, please disregard this message.
                  </p>
                </div>
                <div className="modal-footer">
                  <div className="d-flex w-100 justify-content-between">
                    <div>
                      {/* Remove the old Mark as Resolved button since we now have the dropdown */}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseModal}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard; 